import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { HiOutlineQuestionMarkCircle, HiOutlineX } from "react-icons/hi";
import useHiddenAction from "../../hooks/useHiddenAction";

const POPOVER_MARGIN = 12;
const POPOVER_MAX_WIDTH = 320;
// Estimated popover height used to decide whether to flip above the anchor;
// maxHeight + overflow keep it on screen even when the estimate is off
const POPOVER_FLIP_THRESHOLD = 240;
// Treat empty data-help values as unannotated
const ANNOTATED_SELECTOR = '[data-help]:not([data-help=""])';

const getPopoverStyle = ({ rect, clickX, clickY }) => {
    const width = Math.min(POPOVER_MAX_WIDTH, window.innerWidth - POPOVER_MARGIN * 2);
    const anchorX = rect ? rect.left + rect.width / 2 : clickX;
    const anchorTop = rect ? rect.top : clickY;
    const anchorBottom = rect ? rect.top + rect.height : clickY;
    const left = Math.max(
        POPOVER_MARGIN,
        Math.min(anchorX - width / 2, window.innerWidth - width - POPOVER_MARGIN)
    );

    // Prefer below the anchor; flip above when below lacks room and above has more.
    // The above branch anchors via `bottom` so maxHeight can clamp without transforms.
    const spaceBelow = window.innerHeight - anchorBottom - 8 - POPOVER_MARGIN;
    const spaceAbove = anchorTop - 8 - POPOVER_MARGIN;
    if (spaceBelow < POPOVER_FLIP_THRESHOLD && spaceAbove > spaceBelow) {
        return {
            left,
            width,
            bottom: window.innerHeight - anchorTop + 8,
            maxHeight: Math.max(spaceAbove, 80),
        };
    }
    return { left, width, top: anchorBottom + 8, maxHeight: Math.max(spaceBelow, 80) };
};

const buildPopover = (target, clickX, clickY) => {
    const annotated =
        target instanceof Element ? target.closest(ANNOTATED_SELECTOR) : null;
    const rect = annotated?.getBoundingClientRect();
    return {
        title: annotated?.getAttribute("data-help-title") || "",
        body: annotated?.getAttribute("data-help") || "",
        rect: rect
            ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
            : null,
        clickX,
        clickY,
    };
};

const insideHelpUi = (node) =>
    node instanceof Element && node.closest("[data-help-ui]");

/**
 * HelpMode — hidden click-to-explain mode for the authenticated app.
 *
 * Press Shift+H three times to activate. While active, clicks (and Enter/Space
 * activations) are intercepted instead of acting: any element annotated with
 * `data-help` (and optionally `data-help-title`) gets a popover explaining it.
 * Exit with Escape or the X button in the banner.
 */
const HelpMode = () => {
    const [popover, setPopover] = useState(null);
    const [portalNode, setPortalNode] = useState(null);
    const { isUnlocked: isActive, reset } = useHiddenAction({
        key: "h",
        presses: 3,
        timeout: 2000,
        requireShift: true,
    });

    const exitHelpMode = useCallback(() => {
        setPopover(null);
        reset();
    }, [reset]);

    // The help UI renders into its own element under document.body, outside
    // #root — headlessui's Dialog sets `inert` on the app container while a
    // modal is open, which would otherwise make the banner and popover dead
    useEffect(() => {
        if (!isActive) return;
        const node = document.createElement("div");
        document.body.appendChild(node);
        setPortalNode(node);
        return () => {
            document.body.removeChild(node);
            setPortalNode(null);
        };
    }, [isActive]);

    // Mark the document so CSS can apply the help cursor and hover highlights
    useEffect(() => {
        if (!isActive) return;
        document.body.classList.add("help-mode");
        return () => document.body.classList.remove("help-mode");
    }, [isActive]);

    // Intercept pointer interactions while active: explain instead of acting.
    // Listeners attach to window in the capture phase, which runs before any
    // document/root listener regardless of registration order — so help mode
    // wins even against handlers attached earlier (e.g. an already-open
    // headlessui dialog's outside-click dismissal).
    useEffect(() => {
        if (!isActive) return;

        // stopPropagation (without preventDefault) keeps app handlers from
        // firing while leaving native behavior intact: focus, text selection,
        // and scrollbar dragging still work
        const suppress = (e) => {
            if (insideHelpUi(e.target)) return;
            e.stopPropagation();
        };

        // click/auxclick defaults (link navigation, checkbox toggling,
        // middle-click open-in-tab) must also be prevented
        const handleClick = (e) => {
            if (insideHelpUi(e.target)) return;
            e.preventDefault();
            e.stopPropagation();
            if (e.type !== "click") return;
            setPopover(buildPopover(e.target, e.clientX, e.clientY));
        };

        const suppressed = ["pointerdown", "mousedown", "mouseup", "touchend"];
        suppressed.forEach((type) => window.addEventListener(type, suppress, true));
        window.addEventListener("click", handleClick, true);
        window.addEventListener("auxclick", handleClick, true);
        return () => {
            suppressed.forEach((type) => window.removeEventListener(type, suppress, true));
            window.removeEventListener("click", handleClick, true);
            window.removeEventListener("auxclick", handleClick, true);
        };
    }, [isActive]);

    // Escape closes the popover first, then exits help mode. Enter/Space would
    // activate the focused control for real — intercept and explain it instead.
    useEffect(() => {
        if (!isActive) return;
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                if (popover) {
                    setPopover(null);
                } else {
                    exitHelpMode();
                }
                return;
            }
            if ((e.key === "Enter" || e.key === " ") && !insideHelpUi(e.target)) {
                e.preventDefault();
                e.stopPropagation();
                const rect =
                    e.target instanceof Element ? e.target.getBoundingClientRect() : null;
                const cx = rect?.width ? rect.left + rect.width / 2 : window.innerWidth / 2;
                const cy = rect?.height ? rect.top + rect.height / 2 : window.innerHeight / 3;
                setPopover(buildPopover(e.target, cx, cy));
            }
        };
        window.addEventListener("keydown", handleKeyDown, true);
        return () => window.removeEventListener("keydown", handleKeyDown, true);
    }, [isActive, popover, exitHelpMode]);

    // The popover is anchored to a snapshot of the target's viewport position,
    // so close it as soon as the page scrolls or resizes
    useEffect(() => {
        if (!popover) return;
        const close = () => setPopover(null);
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        return () => {
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
        };
    }, [popover]);

    if (!isActive || !portalNode) return null;

    return createPortal(
        <>
            <div
                data-help-ui
                className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] max-w-[calc(100vw-2rem)] flex items-center gap-3 px-4 py-2 bg-surface-dark text-white rounded-pill shadow-card"
            >
                <HiOutlineQuestionMarkCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-body-md">
                    Help mode — click any element to learn what it does
                </span>
                <span className="hidden sm:inline text-xs text-white/60 border border-white/30 rounded-sm px-1.5 py-0.5">
                    Esc
                </span>
                <button
                    type="button"
                    onClick={exitHelpMode}
                    aria-label="Exit help mode"
                    className="p-1 rounded-pill hover:bg-white/10"
                >
                    <HiOutlineX className="w-4 h-4" />
                </button>
            </div>

            {popover?.rect && (
                <div
                    data-help-ui
                    className="fixed z-[9990] pointer-events-none rounded-sm border-2 border-info-border"
                    style={{
                        top: popover.rect.top - 4,
                        left: popover.rect.left - 4,
                        width: popover.rect.width + 8,
                        height: popover.rect.height + 8,
                    }}
                />
            )}

            {popover && (
                <div
                    data-help-ui
                    role="dialog"
                    aria-label="Help"
                    className="fixed z-[10000] p-4 overflow-y-auto bg-canvas border border-hairline rounded-lg shadow-card"
                    style={getPopoverStyle(popover)}
                >
                    <div className="flex items-start justify-between gap-3 mb-1">
                        <p className="text-label-md text-ink">
                            {popover.title || (popover.body ? "Help" : "No help available")}
                        </p>
                        <button
                            type="button"
                            onClick={() => setPopover(null)}
                            aria-label="Close help popover"
                            className="p-0.5 rounded-sm text-muted hover:text-ink hover:bg-surface-soft"
                        >
                            <HiOutlineX className="w-4 h-4" />
                        </button>
                    </div>
                    {popover.body ? (
                        <p className="text-body-md text-body">{popover.body}</p>
                    ) : (
                        <p className="text-body-md text-body">
                            There is no detailed help for this element yet. The{" "}
                            <Link to="/user-guide" className="underline" onClick={exitHelpMode}>
                                User Guide
                            </Link>{" "}
                            covers every feature in depth.
                        </p>
                    )}
                </div>
            )}
        </>,
        portalNode
    );
};

export default HelpMode;
